import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldAlert, ArrowRight, AlertOctagon, AlertCircle, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const getSeverityIcon = (severity: string) => {
  switch (severity.toLowerCase()) {
    case 'high':
      return AlertOctagon;
    case 'medium':
      return AlertCircle;
    case 'low':
      return ShieldAlert;
    default:
      return AlertOctagon;
  }
};

const getSeverityColor = (severity: string) => {
  switch (severity.toLowerCase()) {
    case 'high':
      return 'border-l-destructive';
    case 'medium':
      return 'border-l-orange-400';
    case 'low':
      return 'border-l-yellow-400';
    default:
      return 'border-l-destructive';
  }
};

const Risks = () => {
  const { data: risks, isLoading } = useQuery({
    queryKey: ['risks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('risk_items')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        throw error;
      }

      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-muted/30">
      <div className="max-w-6xl mx-auto">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-primary">Risks and Mitigation Strategies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              {risks?.map((item, index) => (
                <div key={item.id} className="relative">
                  <div className="flex items-start gap-8 animate-fade-in">
                    {/* Risk Card */}
                    <Card className={`w-[400px] border-l-4 ${getSeverityColor(item.severity)} hover:shadow-lg transition-shadow`}>
                      <CardHeader className="p-4">
                        <div className="flex items-center gap-2">
                          {React.createElement(getSeverityIcon(item.severity), {
                            className: `h-5 w-5 ${item.severity.toLowerCase() === 'high' ? 'text-destructive' : 'text-orange-400'}`
                          })}
                          <CardTitle className="text-lg">
                            {index + 1}. {item.risk}
                          </CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="p-4 pt-0">
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </CardContent>
                    </Card>

                    {/* Arrow Connection */}
                    <div className="flex items-center justify-center w-16">
                      <ArrowRight className="h-6 w-6 text-primary animate-pulse" />
                    </div>

                    {/* Mitigation Card */}
                    <Card className="flex-1 border-l-4 border-l-primary hover:shadow-lg transition-shadow">
                      <CardHeader className="p-4">
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="h-5 w-5 text-primary" />
                          <CardTitle className="text-lg">Mitigation Strategy</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="p-4 pt-0">
                        <p className="text-sm text-muted-foreground">{item.mitigation}</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Risks;